import { Play, Clock, Flame, Dumbbell } from 'lucide-react';
import { Badge } from '../ui/badge';
import { motion } from 'framer-motion';
import { getDifficultyLabel, resolveMediaUrl, getWorkoutThumbnail } from '../../utils/constants';

const isUploadedVideo = (url) => url && (url.startsWith('/api/uploads/videos/') || url.includes('.s3.'));

const difficultyColor = (diff) => {
  switch (diff) {
    case 'beginner': return 'bg-green-500/20 text-green-400 border-green-500/30';
    case 'intermediate': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
    case 'advanced': return 'bg-red-500/20 text-red-400 border-red-500/30';
    default: return 'bg-muted text-muted-foreground';
  }
};

const VideoPlayer = ({ workout, showVideo, onPlay }) => {
  if (!workout.video_url) {
    return (
      <div className="aspect-video relative">
        <img src={workout.image_url} alt={workout.title} className="w-full h-full object-cover" />
      </div>
    );
  }

  const ThumbnailOverlay = ({ className = '' }) => (
    <div
      className={`cursor-pointer group relative ${className}`}
      onClick={onPlay}
      data-testid="video-thumbnail-cover"
    >
      <img src={getWorkoutThumbnail(workout)} alt={workout.title} className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-black/40 flex items-center justify-center group-hover:bg-black/50 transition-colors">
        <div className="h-16 w-16 rounded-full bg-primary/90 flex items-center justify-center group-hover:scale-110 transition-transform">
          <Play className="h-8 w-8 text-black ml-1" />
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden relative card-hover">
      {isUploadedVideo(workout.video_url) ? (
        <>
          <video
            src={resolveMediaUrl(workout.video_url)}
            controls={showVideo}
            autoPlay={showVideo}
            playsInline
            preload={showVideo ? 'auto' : 'metadata'}
            poster={getWorkoutThumbnail(workout)}
            className="w-full bg-black"
            style={{ display: showVideo ? 'block' : 'none' }}
            data-testid="workout-video-player"
          />
          {!showVideo && <ThumbnailOverlay className="w-full" />}
        </>
      ) : showVideo ? (
        <iframe
          src={workout.video_url}
          title={workout.title}
          className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      ) : (
        <ThumbnailOverlay className="w-full h-full" />
      )}
    </div>
  );
};

export const WorkoutVideoPlayer = ({ workout, showVideo, onPlayVideo }) => (
  <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
    <VideoPlayer workout={workout} showVideo={showVideo} onPlay={onPlayVideo} />
    <div className="mt-6">
      <div className="flex items-center gap-3 mb-4">
        <Badge className={`${difficultyColor(workout.difficulty)} border`}>
          {getDifficultyLabel(workout.difficulty)}
        </Badge>
        <Badge className="bg-primary/20 text-primary border border-primary/30">
          {workout.category}
        </Badge>
      </div>
      <h1 className="font-heading text-4xl text-white uppercase tracking-wider mb-3">{workout.title}</h1>
      <p className="text-muted-foreground mb-6">{workout.description}</p>
      <div className="flex items-center gap-6 text-muted-foreground">
        <div className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-primary" />
          <span>{workout.duration_minutes} min</span>
        </div>
        <div className="flex items-center gap-2">
          <Flame className="h-5 w-5 text-primary" />
          <span>{workout.calories_burned} cal</span>
        </div>
        <div className="flex items-center gap-2">
          <Dumbbell className="h-5 w-5 text-primary" />
          <span>{workout.exercises?.length || 0} exercises</span>
        </div>
      </div>
    </div>
  </motion.div>
);
